<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

interface Option {
  label: string;
  value: string | number;
  text?: string;
  hint?: string;
  id?: string;
}

interface Props {
  name: string;
  modelValue: string | number | null | (string | number)[];
  options: Option[] | [];
  placeholder?: string;
  placeholderEmpty?: string;
  selectType?: 'radio' | 'checkbox' | null;
  disabled?: boolean;
  hasOverflow?: boolean;
  isLoading?: boolean;
  hasError?: boolean;
  autoSelectFirst?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  hasOverflow: false,
  isLoading: false,
  hasError: false,
  autoSelectFirst: false,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | (string | number)[]): void;
  (e: 'open'): void;
  (e: 'retry'): void; // Кнопка обновления при ошибке загрузки
}>();

const isOpen = ref(false);
const isEmptyOptions = computed(() => props.options.length === 0);
const selectDisabled = computed(() => props.disabled || isEmptyOptions.value);
const scrollOptions = { wheelPropagation: false, suppressScrollX: true, minScrollbarLength: 50 };

const rootRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const dropdownStyles = ref<{ top: string; left: string; width: string }>({
  top: '0px',
  left: '0px',
  width: '0px',
});

const updateDropdownPosition = () => {
  if (!rootRef.value || !isOpen.value) return;

  const rect = rootRef.value.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;

  const dropdownEl = dropdownRef.value;
  const SPACING = 6;
  const DEFAULT_HEIGHT = 220;
  const dropdownHeight = dropdownEl?.offsetHeight || DEFAULT_HEIGHT;

  const openDown = spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove;

  const top = openDown
    ? rect.bottom + window.scrollY + SPACING
    : rect.top + window.scrollY - dropdownHeight - SPACING;

  dropdownStyles.value = {
    top: `${top}px`,
    left: `${rect.left + window.scrollX}px`,
    width: `${rect.width}px`,
  };
};

const onClickOutside = (event: MouseEvent) => {
  if (!isOpen.value) return;

  const target = event.target as Node | null;

  if (!target) return;
  if (rootRef.value && rootRef.value.contains(target)) return;
  if (dropdownRef.value && dropdownRef.value.contains(target)) return;

  closeSelectDrop();
};

const addClickOutsideListener = () => {
  document.addEventListener('mousedown', onClickOutside);
};

const removeClickOutsideListener = () => {
  document.removeEventListener('mousedown', onClickOutside);
};

const addPositionListeners = () => {
  window.addEventListener('scroll', updateDropdownPosition, true);
  window.addEventListener('resize', updateDropdownPosition);
};

const removePositionListeners = () => {
  window.removeEventListener('scroll', updateDropdownPosition, true);
  window.removeEventListener('resize', updateDropdownPosition);
};

// Получаем отображаемый label выбранного значения (или значений для режима чекбоксов)
const selectedLabel = computed(() => {
  if (Array.isArray(props.modelValue)) {
    if (props.modelValue.length === 0) return null;

    if (props.modelValue.length === 1) {
      const firstValue = props.modelValue[0];
      return props.options.find((o) => o.value === firstValue)?.label;
    }

    return `${props.options.find((o) => o.value === (props.modelValue as (string | number)[])[0])?.label} +${props.modelValue.length - 1}`;
  }
  return props.options.find((o) => o.value === props.modelValue)?.label;
});

// Текст в поле: для пустого списка — placeholderEmpty, иначе выбранное значение или placeholder
const fieldLabel = computed(() => {
  if (isEmptyOptions.value && props.placeholderEmpty) return props.placeholderEmpty;
  return selectedLabel.value ?? props.placeholder ?? 'Выберите значение';
});

const isSelected = computed(() => (value: string | number) => {
  if (Array.isArray(props.modelValue)) {
    return props.modelValue.includes(value);
  }
  return props.modelValue === value;
});

// Обрабатываем нажатие на опцию (radio/checkbox режим)
const select = (option: Option) => {
  if (props.selectType === 'checkbox') {
    const currentValue = Array.isArray(props.modelValue) ? props.modelValue : [];
    const index = currentValue.indexOf(option.value);

    if (index > -1) {
      const newValue = currentValue.filter((v) => v !== option.value);
      emit('update:modelValue', newValue);
    } else {
      emit('update:modelValue', [...currentValue, option.value]);
    }
  } else {
    emit('update:modelValue', option.value);
    isOpen.value = false;
  }
};

// Следим за изменением списка: при включённом autoSelectFirst первая опция выбирается автоматически
watch(
  () => props.options,
  (newOptions) => {
    if (!props.autoSelectFirst || newOptions.length === 0) return;

    const firstValue = newOptions[0].value;
    if (props.selectType === 'checkbox') {
      if (Array.isArray(props.modelValue) && props.modelValue.length === 0) {
        emit('update:modelValue', [firstValue]);
      }
    } else if (!props.modelValue) {
      emit('update:modelValue', firstValue);
    }
  },
  { immediate: true },
);

const toggleOpen = () => {
  if (selectDisabled.value) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    emit('open');
    nextTick(() => {
      updateDropdownPosition();
      addPositionListeners();
      addClickOutsideListener();
    });
  } else {
    removePositionListeners();
    removeClickOutsideListener();
  }
};

const closeSelectDrop = () => {
  isOpen.value = false;
  removePositionListeners();
  removeClickOutsideListener();
};

watch(
  () => [props.options.length, props.isLoading],
  () => {
    if (isOpen.value) {
      nextTick(() => {
        updateDropdownPosition();
      });
    }
  },
);

onMounted(() => {
  if (isOpen.value) {
    nextTick(() => {
      updateDropdownPosition();
      addPositionListeners();
      addClickOutsideListener();
    });
  }
});

onBeforeUnmount(() => {
  removePositionListeners();
  removeClickOutsideListener();
});
</script>

<template>
  <div
    ref="rootRef"
    class="custom-select"
    :class="[selectType, { 'is-open': isOpen, 'is-disabled': selectDisabled, 'is-loading': isLoading }]"
    tabindex="0"
  >
    <input type="text" :name="name" :value="modelValue" autocomplete="off" />
    <div class="field" @click="toggleOpen" :class="{ 'not-selected': !selectedLabel }"
         :data-qa="`select-${name}-field-toggle`">
      <div class="option-label">
        {{ fieldLabel }}
      </div>
      <span class="arrow"></span>
    </div>

    <Teleport to="body">
      <div
        ref="dropdownRef"
        class="dropdown"
        :class="{ 'has-overflow': hasOverflow }"
        v-if="isOpen"
        :style="dropdownStyles"
      >
      <slot name="loader"></slot>
      <template v-if="isLoading">
        <div class="loader-alt">
          <span class="loader-alt-spinner"></span>
        </div>
      </template>
      <template v-if="!isLoading && hasError">
        <div class="error-message">
          <div>Ошибка загрузки данных</div>
          <button class="btn-refresh" type="button" @click="emit('retry')"
                  :data-qa="`select-${name}-button-retry`">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="#30A8FF" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M15.3331 7.33333C15.1563 7.33333 14.9868 7.40357 14.8617 7.5286C14.7367 7.65362 14.6665 7.82319 14.6665 8C14.6719 9.55018 14.1388 11.0541 13.1582 12.2548C12.1776 13.4554 10.8104 14.2782 9.29041 14.5825C7.77038 14.8869 6.19185 14.6538 4.82461 13.9232C3.45737 13.1927 2.38631 12.0099 1.7945 10.5771C1.2027 9.14433 1.12689 7.55049 1.58003 6.06801C2.03317 4.58552 2.98713 3.30644 4.27885 2.44938C5.57057 1.59232 7.11986 1.2105 8.66191 1.36918C10.204 1.52786 11.643 2.21718 12.7331 3.31933C12.7112 3.32531 12.689 3.32999 12.6665 3.33333H10.6665C10.4897 3.33333 10.3201 3.40357 10.1951 3.5286C10.0701 3.65362 9.99982 3.82319 9.99982 4C9.99982 4.17681 10.0701 4.34638 10.1951 4.4714C10.3201 4.59643 10.4897 4.66667 10.6665 4.66667H12.6665C13.1969 4.66667 13.7056 4.45595 14.0807 4.08088C14.4558 3.70581 14.6665 3.1971 14.6665 2.66667V0.666667C14.6665 0.489856 14.5962 0.320286 14.4712 0.195262C14.3462 0.0702379 14.1766 0 13.9998 0C13.823 0 13.6534 0.0702379 13.5284 0.195262C13.4034 0.320286 13.3331 0.489856 13.3331 0.666667V2.04333C11.9682 0.82302 10.2258 0.108111 8.39705 0.0180684C6.56834 -0.0719742 4.76411 0.468305 3.28587 1.54862C1.80762 2.62894 0.744905 4.18386 0.275299 5.95354C-0.194308 7.72322 -0.0423553 9.60046 0.705764 11.2716C1.45388 12.9427 2.75285 14.3064 4.3856 15.1349C6.01836 15.9634 7.88599 16.2065 9.67641 15.8235C11.4668 15.4405 13.0716 14.4547 14.2225 13.0307C15.3734 11.6067 16.0008 9.83093 15.9998 8C15.9998 7.82319 15.9296 7.65362 15.8046 7.5286C15.6795 7.40357 15.51 7.33333 15.3331 7.33333Z"
              ></path>
            </svg>
            <span>Обновить</span>
          </button>
        </div>
      </template>
      <template v-if="!isLoading && options.length > 0 && !hasError">
        <PerfectScrollbar class="dropdown-scroll" :options="scrollOptions">
          <ul>
            <li
              v-for="option in options"
              :key="option.id"
              :data-id="option.id"
              :class="{ active: isSelected(option.value) }"
              @click="select(option)"
              :data-qa="`select-${name}-option-choose`"
            >
              <div class="dropdown-label">{{ option.label }}</div>
              <div v-if="option.text">
                <span>{{ option.text }}</span>
              </div>
            </li>
          </ul>
        </PerfectScrollbar>
      </template>
    </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.custom-select {
  position: relative;
  min-width: 100%;

  input {
    display: none;
  }

  &:not(.radio):not(.checkbox) {
    li:hover {
      background-color: rgba(242, 242, 242, 1);
    }
  }

  &.radio {
    li {
      padding-left: 36px;
    }

    li.active::before {
      background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3QgeD0iMC41IiB5PSIwLjUiIHdpZHRoPSIxOSIgaGVpZ2h0PSIxOSIgcng9IjkuNSIgZmlsbD0id2hpdGUiLz4KPHJlY3QgeD0iMC41IiB5PSIwLjUiIHdpZHRoPSIxOSIgaGVpZ2h0PSIxOSIgcng9IjkuNSIgc3Ryb2tlPSIjMzBBOEZGIi8+CjxjaXJjbGUgY3g9IjEwIiBjeT0iMTAiIHI9IjciIGZpbGw9IiMzMEE4RkYiLz4KPC9zdmc+Cg==');
    }

    li:not(.active):hover::before {
      background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3QgeD0iMC41IiB5PSIwLjUiIHdpZHRoPSIxOSIgaGVpZ2h0PSIxOSIgcng9IjkuNSIgZmlsbD0id2hpdGUiLz4KPHJlY3QgeD0iMC41IiB5PSIwLjUiIHdpZHRoPSIxOSIgaGVpZ2h0PSIxOSIgcng9IjkuNSIgc3Ryb2tlPSIjOTdEM0ZGIi8+CjxjaXJjbGUgb3BhY2l0eT0iMC41IiBjeD0iMTAiIGN5PSIxMCIgcj0iNyIgZmlsbD0iIzMwQThGRiIvPgo8L3N2Zz4K');
    }

    li::before {
      content: '';
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 8px;
      width: 20px;
      height: 20px;
      background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3QgeD0iMC41IiB5PSIwLjUiIHdpZHRoPSIxOSIgaGVpZ2h0PSIxOSIgcng9IjkuNSIgZmlsbD0id2hpdGUiLz4KPHJlY3QgeD0iMC41IiB5PSIwLjUiIHdpZHRoPSIxOSIgaGVpZ2h0PSIxOSIgcng9IjkuNSIgc3Ryb2tlPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo=');
      background-position: center center;
      transition: background-image 0.2s ease;
    }
  }

  &.checkbox {
    li {
      padding-left: 36px;
    }

    li.active::before {
      background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3QgeD0iMC41IiB5PSIwLjUiIHdpZHRoPSIxOSIgaGVpZ2h0PSIxOSIgcng9IjIuNSIgZmlsbD0id2hpdGUiLz4KPHJlY3QgeD0iMC41IiB5PSIwLjUiIHdpZHRoPSIxOSIgaGVpZ2h0PSIxOSIgcng9IjIuNSIgc3Ryb2tlPSIjQ0NDQ0NDIi8+CjxwYXRoIGQ9Ik03LjU1MDEgMTUuODc1TDEuODUwMSAxMC4xNzVMMy4wMDAxMiA5LjAyNDk1TDcuNTUwMTUgMTMuNTc1TDE3LjAwMDEgNC4xMjVMMTguMTUwMSA1LjI3NDk4TDcuNTUwMSAxNS44NzVaIiBmaWxsPSIjMzBBOEZGIi8+Cjwvc3ZnPgo=');
    }

    li:not(.active):hover::before {
      background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3QgeD0iMC41IiB5PSIwLjUiIHdpZHRoPSIxOSIgaGVpZ2h0PSIxOSIgcng9IjIuNSIgZmlsbD0id2hpdGUiLz4KPHJlY3QgeD0iMC41IiB5PSIwLjUiIHdpZHRoPSIxOSIgaGVpZ2h0PSIxOSIgcng9IjIuNSIgc3Ryb2tlPSIjMzBhOGZmIi8+Cjwvc3ZnPgo=');
    }

    li::before {
      content: '';
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 8px;
      width: 20px;
      height: 20px;
      background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3QgeD0iMC41IiB5PSIwLjUiIHdpZHRoPSIxOSIgaGVpZ2h0PSIxOSIgcng9IjIuNSIgZmlsbD0id2hpdGUiLz4KPHJlY3QgeD0iMC41IiB5PSIwLjUiIHdpZHRoPSIxOSIgaGVpZ2h0PSIxOSIgcng9IjIuNSIgc3Ryb2tlPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo=');
      background-position: center center;
      transition: background-image 0.2s ease;
    }
  }

  &.is-open {
    .field {
      border-color: rgba(48, 168, 255, 1);
    }

    .arrow {
      opacity: 1;
      transform: rotate(180deg);
    }
  }

  &.is-disabled {
    pointer-events: none;  

    .arrow {
      opacity: 0;
    }

    .field {
      opacity: 1;
      border-color: rgba(229, 229, 229, 1);
      color: rgba(229, 229, 229, 1);
    }
  }

  &.is-loading {
    pointer-events: none;
    background-color: rgb(244, 244, 244);
  }
}

.field {
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  user-select: none;
  padding: 7px 10px;
  border: 1px solid #CCCCCC;
  border-radius: 3px;
  display: flex;
  justify-content: space-between;
  width: 100%;
  min-width: 100%;
  font-size: 13px;
  font-weight: 500;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: rgba(48, 168, 255, 1);
  }
}

.option-label {
  line-clamp: 1;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown {
  position: absolute;
  border: 1px solid #ccc;
  background-color: white;
  list-style: none;
  padding: 0;
  margin: 0;
  z-index: 99999;
  border-radius: 3px;
  box-shadow: -1px 0px 12px 0px rgba(0, 0, 0, 0.15);

  &.has-overflow .ps {
    overflow: visible !important;
  }

  .loader-alt,
  .error-message {
    padding: 20px 0;
  }

  .error-message {
    display: flex;
    align-items: center;
    flex-direction: column;
    text-align: center;
    font-size: 13px;
    padding-bottom: 16px;
  }

  &-scroll {
    max-height: 189px;
  }

  &:not(.radio):not(.checkbox) {
    li:hover {
      background-color: rgba(242, 242, 242, 1);
    }
  }

  li {
    position: relative;
    padding: 10px 8px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-size: 13px;
    line-height: 1.2;
    letter-spacing: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    row-gap: 4px;
    column-gap: 10px;
    flex-wrap: wrap;
    user-select: none;
    min-height: 38px;

    &:not(:last-child) {
      border-bottom: 1px solid rgba(224, 224, 230, 1);
    }

    span {
      color: rgba(136, 136, 136, 1);
    }
  }
}

.dropdown-label {
  line-height: inherit;
}

.not-selected {
  color: rgba(194, 194, 194, 1);
  user-select: none;
}

.arrow {
  display: block;
  width: 10px;
  height: 6px;
  background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iNiIgdmlld0JveD0iMCAwIDEwIDYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik01IDZMMCAwLjk0MjY1MUwwLjkzMTk1OCAwTDUgNC4xMTQ3Mkw5LjA2ODA0IDBMMTAgMC45NDI2NTFMNSA2WiIgZmlsbD0iIzg4ODg4OCIvPgo8L3N2Zz4K');
  background-repeat: no-repeat;
  background-position: center center;
  margin-top: auto;
  margin-bottom: auto;
}

.btn-refresh {
  font-family: 'Montserrat', sans-serif;
  background-color: transparent;
  color: #30a8ff;
  border: none;
  padding: 11px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;

  svg {
    width: 16px;
    height: 16px;
    min-width: 16px;
  }
}
</style>
